import { useRef, useState, useCallback } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import PhoenixOffcanvas, { PhoenixOffcanvasContainer } from 'components/base/PhoenixOffcanvas';
import ConversationDetails from '../conversation-details';
import { useBreakpoints } from 'providers/BreakpointsProvider';
import ChatContentFooter from './ChatContentFooter';
import Message from '../message';
import Scrollbar from 'components/base/Scrollbar';
import { useParams } from 'react-router-dom';
import api from 'app/api/axios-api';

// Import the exact types from data/chat
import { Message as ChatMessage, User as ChatUser, Conversation as ChatConversation } from 'data/chat';

// Define message type for API response
const ChatContentLifeSciences = () => {
  const { userId } = useParams<{ userId: string }>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showConversationDetails, setShowConversationDetails] = useState(false);
  const [showUserListOffcanvas] = useState(false);
  const [loading, setLoading] = useState(false);

  const { breakpoints } = useBreakpoints();
  const messageEndRef = useRef<HTMLSpanElement>(null);

  const user: ChatUser = {
    id: 1,
    name: userId || 'User',
    avatar: '',
    status: 'online'
  };

  const currentConversation: ChatConversation = {
    id: 1,
    user: user,
    messages: messages
  };

  const postQuestion = useCallback(async (questionText: string) => {
    if (!questionText) return;

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: Date.now(),
      message: questionText,
      type: 'sent',
      time: new Date().toLocaleTimeString(),
      readAt: null,
      attachments: undefined
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      const response = await api.post('lifesciences/ask', {
        question: questionText
      });

      const botResponse: ChatMessage = {
        id: Date.now() + 1,
        message: response.data.response || 'No response received.',
        type: 'received',
        time: new Date().toLocaleTimeString(),
        readAt: null,
        attachments: undefined
      };
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error('Error sending question:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Card
      as={PhoenixOffcanvasContainer}
      className="w-100 d-flex flex-column"
      style={{
        maxHeight: '50vh',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(15, 23, 42, 0.08)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        backgroundColor: '#ffffff'
      }}
    >
      <Card.Body
        className="d-flex flex-column flex-grow-1 flex-shrink-1"
        style={{ minHeight: 0, overflow: 'hidden', padding: '16px 18px 14px' }}
      >
        <div
          className="scrollbar d-flex flex-column gap-2 flex-grow-1"
          style={{
            minHeight: '200px',
            overflowY: 'auto',
            backgroundColor: '#f8fafc',
            border: '1px solid #eef2f7',
            borderRadius: '10px',
            padding: '14px 16px'
          }}
        >
          {currentConversation.messages.length === 0
            ? null
            : currentConversation.messages.map(message => (
                <Message key={message.id} message={message} user={currentConversation.user} />
              ))}

          {/* Show loading spinner in chat content while waiting for a response */}
          {loading && (
            <div className="text-center mt-3">
              <Spinner animation="border" />
            </div>
          )}

          <span ref={messageEndRef} />
        </div>
      </Card.Body>
      {ChatContentFooter && <ChatContentFooter onSend={postQuestion} minimal />}
      <PhoenixOffcanvas
        open={showConversationDetails}
        placement="top"
        noBackdrop
        className="bg-body-emphasis w-100 z-index-0 rounded-3"
      >
        <Scrollbar>
          <ConversationDetails
            conversation={currentConversation}
            handleClose={() => setShowConversationDetails(false)}
          />
        </Scrollbar>
      </PhoenixOffcanvas>
      {breakpoints.down('sm') && (
        <PhoenixOffcanvas open={showUserListOffcanvas} placement="start" noBackdrop className="w-100 z-index-0" />
      )}
    </Card>
  );
};

export default ChatContentLifeSciences;
